/* LEGEND perf instrument — answers "what is eating the main thread".
 *
 * Four independent probes, so the culprit shows up whichever layer it lives in:
 *   1. rAF frame clock       — fps, worst frame, jank (>50ms gaps between frames)
 *   2. Long Task observer    — every main-thread block >50ms, whatever caused it
 *   3. WS dispatch timing    — per frame-type: msgs/s, KB/s, main-thread ms/s.
 *                              Wired in ws.ts around JSON.parse + dispatch, so it
 *                              includes every subscriber callback that runs
 *                              synchronously (chart series updates, mappers, …).
 *                              React state set in a handler flushes LATER via the
 *                              scheduler — that cost shows up in probe 4, not here.
 *   4. React <Profiler> agg  — per wrapped subtree: commits/s, render ms/s.
 *                              Fires in dev builds out of the box. For a prod
 *                              bundle, build with LEGEND_PROFILE=1 (vite aliases
 *                              react-dom/client → react-dom/profiling).
 * Plus 1 Hz gauges: JS heap (Chrome only), DOM node count, DOM mutations/s
 * (catches the direct-textContent-poke paths that React profiling can't see).
 *
 * Enable: append ?perf to the URL, or Ctrl+Alt+P in the tab (persists in
 * localStorage under legend:perf). The HUD paints bottom-right at 1 Hz via
 * direct DOM — it never enters the React tree and takes no pointer events.
 * Console: __legendPerf.report() · .snapshot() · .reset() · .toggle().
 * Off = a single boolean check per WS frame; nothing else runs.
 *
 * Black box: while enabled, a snapshot is persisted to localStorage every 5s,
 * so a hard freeze / killed tab loses at most 5s of data. After reloading,
 * __legendPerf.lastRun() returns the final snapshot of the PREVIOUS run.
 */
import React from 'react';

type Agg = { n: number; ms: number; bytes: number; max: number };
type Bucket = {
  t: number;
  ws: Map<string, Agg>;
  react: Map<string, Agg>;
  frames: number; worstFrame: number; jank: number;
  ltN: number; ltMs: number; ltMax: number;
  mut: number;
};

const WINDOW = 10; // HUD shows rates over the last ~10 one-second buckets
const KEY_LAST = 'legend:perf:last'; // black box — this run, rewritten every 5s
const KEY_PREV = 'legend:perf:prev'; // black box — final snapshot of the run before

function newBucket(): Bucket {
  return {
    t: Date.now(), ws: new Map(), react: new Map(),
    frames: 0, worstFrame: 0, jank: 0, ltN: 0, ltMs: 0, ltMax: 0, mut: 0,
  };
}

function bump(m: Map<string, Agg>, k: string, n: number, ms: number, bytes: number) {
  let a = m.get(k);
  if (!a) { a = { n: 0, ms: 0, bytes: 0, max: 0 }; m.set(k, a); }
  a.n += n; a.ms += ms; a.bytes += bytes; if (ms > a.max) a.max = ms;
}

function top(m: Map<string, Agg>, n: number): Array<[string, Agg]> {
  return Array.from(m.entries()).sort((a, b) => b[1].ms - a[1].ms).slice(0, n);
}

class Perf {
  on = false;

  private hud: HTMLDivElement | null = null;
  private ring: Bucket[] = [];
  private cur = newBucket();
  private wsTotal = new Map<string, Agg>();
  private reactTotal = new Map<string, Agg>();
  private ltTotal = { n: 0, ms: 0, max: 0 };
  private startedAt = 0;
  private raf = 0;
  private lastFrame = 0;
  private obs: PerformanceObserver | null = null;
  private mo: MutationObserver | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Call once at boot (main.tsx). Cheap when perf mode is not requested. */
  init() {
    try {
      window.addEventListener('keydown', (e) => {
        if (e.ctrlKey && e.altKey && (e.key === 'p' || e.key === 'P' || e.code === 'KeyP')) {
          e.preventDefault();
          this.toggle();
        }
      });
      document.addEventListener('visibilitychange', () => { this.lastFrame = 0; }); // don't count the away-gap as a frame
      (window as any).__legendPerf = {
        toggle: () => this.toggle(),
        report: () => this.report(),
        snapshot: () => this.snapshot(),
        reset: () => this.reset(),
        lastRun: () => this.lastRun(),
      };
      const want = new URLSearchParams(location.search).has('perf')
        || localStorage.getItem('legend:perf') === '1';
      if (want) this.enable();
    } catch { /* the instrument must never take the app down */ }
  }

  toggle() { this.on ? this.disable() : this.enable(); }

  enable() {
    if (this.on) return;
    this.on = true;
    this.startedAt = Date.now();
    try {
      localStorage.setItem('legend:perf', '1');
      const prev = localStorage.getItem(KEY_LAST); // preserve the dead run's black box
      if (prev) localStorage.setItem(KEY_PREV, prev);
    } catch { /* private mode */ }

    this.lastFrame = 0;
    this.raf = requestAnimationFrame(this.frameLoop);

    if (typeof PerformanceObserver !== 'undefined') {
      try {
        this.obs = new PerformanceObserver((list) => {
          for (const e of list.getEntries()) {
            this.cur.ltN++; this.cur.ltMs += e.duration;
            if (e.duration > this.cur.ltMax) this.cur.ltMax = e.duration;
            this.ltTotal.n++; this.ltTotal.ms += e.duration;
            if (e.duration > this.ltTotal.max) this.ltTotal.max = e.duration;
          }
        });
        this.obs.observe({ type: 'longtask', buffered: false } as any);
      } catch { this.obs = null; /* longtask unsupported (Safari/FF) — other probes still run */ }
    }

    try {
      this.mo = new MutationObserver((recs) => { this.cur.mut += recs.length; });
      this.mo.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true });
    } catch { this.mo = null; }

    this.timer = setInterval(() => this.rotate(), 1000);
    this.mountHud();
  }

  disable() {
    if (!this.on) return;
    this.on = false;
    try { localStorage.removeItem('legend:perf'); } catch { /* ignore */ }
    cancelAnimationFrame(this.raf);
    if (this.obs) { this.obs.disconnect(); this.obs = null; }
    if (this.mo) { this.mo.disconnect(); this.mo = null; }
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    if (this.hud) { this.hud.remove(); this.hud = null; }
  }

  reset() {
    this.ring = [];
    this.cur = newBucket();
    this.wsTotal = new Map();
    this.reactTotal = new Map();
    this.ltTotal = { n: 0, ms: 0, max: 0 };
    this.startedAt = Date.now();
  }

  // ---- probe feeds ----

  /** ws.ts: one data frame fully dispatched (parse + all synchronous handlers). */
  wsFrame(type: string, bytes: number, ms: number) {
    if (!this.on) return;
    bump(this.cur.ws, type, 1, ms, bytes);
    bump(this.wsTotal, type, 1, ms, bytes);
  }

  /** React <Profiler> onRender — aggregated per wrapped subtree id. */
  onRender = (id: string, _phase: string, actualDuration: number) => {
    if (!this.on) return;
    bump(this.cur.react, id, 1, actualDuration, 0);
    bump(this.reactTotal, id, 1, actualDuration, 0);
  };

  // ---- internals ----

  private frameLoop = (ts: number) => {
    if (!this.on) return;
    if (this.lastFrame && !document.hidden) {
      const gap = ts - this.lastFrame;
      this.cur.frames++;
      if (gap > this.cur.worstFrame) this.cur.worstFrame = gap;
      if (gap > 50) this.cur.jank++;
    }
    this.lastFrame = ts;
    this.raf = requestAnimationFrame(this.frameLoop);
  };

  private ticks = 0;

  private rotate() {
    this.ring.push(this.cur);
    if (this.ring.length > WINDOW) this.ring.shift();
    this.cur = newBucket();
    this.paint();
    if (++this.ticks % 5 === 0) {
      try { localStorage.setItem(KEY_LAST, JSON.stringify(this.snapshot())); } catch { /* quota/private */ }
    }
  }

  /** Final snapshot the PREVIOUS perf run persisted — survives a killed tab. */
  lastRun() {
    try { const s = localStorage.getItem(KEY_PREV); return s ? JSON.parse(s) : null; } catch { return null; }
  }

  /** Sum the ring into one window view; secs = real span the ring covers. */
  private windowSums() {
    const ws = new Map<string, Agg>();
    const react = new Map<string, Agg>();
    let frames = 0, worstFrame = 0, jank = 0, ltN = 0, ltMs = 0, ltMax = 0, mut = 0;
    for (const b of this.ring) {
      b.ws.forEach((a, k) => { bump(ws, k, a.n, a.ms, a.bytes); const t = ws.get(k)!; if (a.max > t.max) t.max = a.max; });
      b.react.forEach((a, k) => { bump(react, k, a.n, a.ms, a.bytes); const t = react.get(k)!; if (a.max > t.max) t.max = a.max; });
      frames += b.frames; jank += b.jank; ltN += b.ltN; ltMs += b.ltMs; mut += b.mut;
      if (b.worstFrame > worstFrame) worstFrame = b.worstFrame;
      if (b.ltMax > ltMax) ltMax = b.ltMax;
    }
    const secs = this.ring.length
      ? Math.max(1, (this.ring[this.ring.length - 1].t - this.ring[0].t) / 1000 + 1)
      : 1;
    return { ws, react, frames, worstFrame, jank, ltN, ltMs, ltMax, mut, secs };
  }

  private mountHud() {
    if (this.hud) return;
    const d = document.createElement('div');
    d.id = 'legend-perf-hud';
    d.style.cssText = [
      'position:fixed', 'right:8px', 'bottom:8px', 'z-index:2147483647',
      'background:rgba(8,10,14,.88)', 'color:#9fd0ff', 'border:1px solid #2a3a55',
      'border-radius:6px', 'padding:8px 10px', 'pointer-events:none',
      'font:10.5px/1.5 ui-monospace,Menlo,Consolas,monospace', 'white-space:pre',
      'max-width:360px', 'overflow:hidden',
    ].join(';');
    d.textContent = 'perf: collecting…';
    document.body.appendChild(d);
    this.hud = d;
  }

  private paint() {
    if (!this.hud) return;
    const w = this.windowSums();
    const fps = w.frames / w.secs;
    const heap = (performance as any).memory
      ? Math.round((performance as any).memory.usedJSHeapSize / 1048576) + 'MB' : 'n/a';
    const dom = document.getElementsByTagName('*').length;

    const L: string[] = [];
    L.push(`LEGEND PERF · last ${Math.round(w.secs)}s · Ctrl+Alt+P`);
    L.push(`fps ${fps.toFixed(1).padStart(5)} · worst ${Math.round(w.worstFrame)}ms · jank>50ms ${w.jank}`);
    L.push(`long tasks ${w.ltN} · ${Math.round(w.ltMs)}ms · max ${Math.round(w.ltMax)}ms`);
    L.push(`heap ${heap} · dom ${dom} · mut/s ${(w.mut / w.secs).toFixed(0)}`);
    L.push('── ws type      msg/s   kb/s  ms/s  max');
    for (const [k, a] of top(w.ws, 6)) {
      L.push(`${k.slice(0, 12).padEnd(12)}${(a.n / w.secs).toFixed(0).padStart(6)}${(a.bytes / 1024 / w.secs).toFixed(0).padStart(7)}${(a.ms / w.secs).toFixed(1).padStart(6)}${Math.round(a.max).toString().padStart(5)}`);
    }
    if (!w.ws.size) L.push('  (no ws frames)');
    L.push('── react        cmt/s   ms/s  max');
    for (const [k, a] of top(w.react, 6)) {
      L.push(`${k.slice(0, 12).padEnd(12)}${(a.n / w.secs).toFixed(0).padStart(6)}${(a.ms / w.secs).toFixed(1).padStart(7)}${Math.round(a.max).toString().padStart(5)}`);
    }
    if (!w.react.size) L.push('  (none — dev or LEGEND_PROFILE build only)');
    this.hud.textContent = L.join('\n');
  }

  /** Console dump — session totals since enable()/reset(), plus the 10s window. */
  report() {
    const mins = ((Date.now() - this.startedAt) / 60000).toFixed(1);
    const rows = (m: Map<string, Agg>, withBytes: boolean) =>
      Array.from(m.entries())
        .sort((a, b) => b[1].ms - a[1].ms)
        .map(([k, a]) => ({
          id: k, count: a.n,
          ...(withBytes ? { kb: +(a.bytes / 1024).toFixed(1) } : {}),
          'main-ms': +a.ms.toFixed(1), 'max-ms': +a.max.toFixed(1),
        }));
    /* eslint-disable no-console */
    console.group(`LEGEND PERF — session totals (${mins} min)`);
    console.log(`long tasks: ${this.ltTotal.n} · ${Math.round(this.ltTotal.ms)}ms blocked · max ${Math.round(this.ltTotal.max)}ms`);
    console.log('WS dispatch (parse + sync handlers), by frame type:');
    console.table(rows(this.wsTotal, true));
    console.log('React commits, by wrapped subtree (parents include children):');
    if (this.reactTotal.size) console.table(rows(this.reactTotal, false));
    else console.log('  none recorded — React attribution needs a dev build or LEGEND_PROFILE=1 prod build');
    console.groupEnd();
    /* eslint-enable no-console */
    return this.snapshot();
  }

  /** Structured dump of everything — for pasting into an issue or a chat. */
  snapshot() {
    const w = this.windowSums();
    const obj = (m: Map<string, Agg>) => {
      const o: Record<string, Agg> = {};
      m.forEach((a, k) => { o[k] = { ...a, ms: +a.ms.toFixed(1), max: +a.max.toFixed(1) }; });
      return o;
    };
    return {
      at: new Date().toISOString(),
      sessionMin: +((Date.now() - this.startedAt) / 60000).toFixed(1),
      window: {
        secs: +w.secs.toFixed(1), fps: +(w.frames / w.secs).toFixed(1),
        worstFrameMs: Math.round(w.worstFrame), jank: w.jank,
        longTasks: { n: w.ltN, ms: Math.round(w.ltMs), max: Math.round(w.ltMax) },
        mutPerSec: +(w.mut / w.secs).toFixed(0),
        ws: obj(w.ws), react: obj(w.react),
      },
      session: { longTasks: { ...this.ltTotal, ms: Math.round(this.ltTotal.ms), max: Math.round(this.ltTotal.max) }, ws: obj(this.wsTotal), react: obj(this.reactTotal) },
      heapMB: (performance as any).memory ? Math.round((performance as any).memory.usedJSHeapSize / 1048576) : null,
      domNodes: document.getElementsByTagName('*').length,
    };
  }
}

export const perf = new Perf();

/** Wrap a subtree in a React <Profiler> feeding the aggregator. Zero-config in
 *  dev; in a plain prod bundle onRender simply never fires. Keep ids stable —
 *  a parent id's time includes its wrapped children. */
export function PP(id: string, el: React.ReactElement | null): React.ReactElement | null {
  if (el == null) return el;
  return React.createElement(React.Profiler, { id, onRender: perf.onRender }, el);
}
